import { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Shard, ShardStatus, specProgress } from '../types/shard'
import { Worker } from '../hooks/useWorkers'
import { PersonaIcon } from './PersonaIcon'

interface SpecsTreeProps {
  shards: Shard[]
  workers: Worker[]
  onShardClick: (shard: Shard) => void
  onWorkerClick: (worker: Worker) => void
}

interface SpecGroup {
  spec: Shard
  tasks: Shard[]
}

// Status icon mapping
function statusIcon(status: ShardStatus): string {
  switch (status) {
    case 'DONE': case 'APPROVED': case 'FINAL_APPROVED': return '\u2713'
    case 'IN_PROGRESS': return '\u25CF'
    case 'BLOCKED': case 'DESIGN_REJECTED': case 'REJECTED': return '\u2715'
    case 'REVIEW': case 'FINAL_REVIEW': case 'PENDING_APPROVAL': case 'DESIGN_PENDING': return '\u25CE'
    default: return '\u25CB'
  }
}

function statusIconColor(status: ShardStatus): string {
  switch (status) {
    case 'DONE': case 'APPROVED': case 'FINAL_APPROVED': return 'text-gray-400'
    case 'IN_PROGRESS': case 'DESIGN_APPROVED': return 'text-teal-400'
    case 'BLOCKED': case 'DESIGN_REJECTED': case 'REJECTED': return 'text-cosmic-plasma'
    case 'REVIEW': case 'FINAL_REVIEW': case 'PENDING_APPROVAL': case 'DESIGN_PENDING': return 'text-purple-400'
    default: return 'text-cosmic-star'
  }
}

// Dependency edge colors
const DEP_COLORS = [
  '#8b5cf6', '#06b6d4', '#f59e0b', '#ec4899', '#10b981',
  '#6366f1', '#f97316', '#14b8a6', '#a855f7', '#ef4444',
]

export function SpecsTree({ shards, workers, onShardClick, onWorkerClick }: SpecsTreeProps) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [selectedShardId, setSelectedShardId] = useState<string | null>(null)
  const [highlightedShardId, setHighlightedShardId] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const treeRef = useRef<HTMLDivElement>(null)
  const rowRefs = useRef<Map<string, HTMLElement>>(new Map())
  const [depLines, setDepLines] = useState<{ from: string; to: string; color: string; y1: number; y2: number }[]>([])
  const [treeScrollHeight, setTreeScrollHeight] = useState(0)

  // Build SPEC -> TASK tree with fallback grouping (sorted ascending by ID number)
  const specGroups = useMemo(() => {
    const sortById = (a: Shard, b: Shard) => {
      const numA = parseInt(a.id.replace(/\D+/g, ''), 10) || 0
      const numB = parseInt(b.id.replace(/\D+/g, ''), 10) || 0
      return numA - numB
    }
    const specs = shards.filter(s => s.id.startsWith('SPEC')).sort(sortById)
    const tasks = shards.filter(s => !s.id.startsWith('SPEC')).sort(sortById)
    const groups: SpecGroup[] = []

    // Check if any task has parent_id set
    const hasParentIds = tasks.some(t => t.parent_id)

    if (hasParentIds) {
      // Group tasks by parent_id
      const tasksByParent = new Map<string, Shard[]>()
      for (const task of tasks) {
        const parentId = task.parent_id || 'orphan'
        const existing = tasksByParent.get(parentId) || []
        existing.push(task)
        tasksByParent.set(parentId, existing)
      }

      for (const spec of specs) {
        groups.push({
          spec,
          tasks: tasksByParent.get(spec.id) || [],
        })
      }

      // Orphan tasks
      const orphanTasks = tasksByParent.get('orphan') || []
      if (orphanTasks.length > 0) {
        groups.push({
          spec: { id: 'ORPHAN', title: 'Unlinked Tasks', status: 'TODO' as ShardStatus } as Shard,
          tasks: orphanTasks,
        })
      }
    } else {
      // Fallback: no parent_id on any task.
      // If exactly one SPEC, assign all tasks to it.
      // If multiple SPECs, try to infer from depends_on chains.
      if (specs.length === 1) {
        groups.push({ spec: specs[0]!, tasks })
      } else if (specs.length > 1) {
        // Try to infer: walk depends_on chains. If a task depends on another
        // task already assigned to a spec, assign to the same spec.
        const taskSpecMap = new Map<string, string>()
        const specIds = new Set(specs.map(s => s.id))

        // First pass: any task whose depends_on includes a SPEC id
        for (const task of tasks) {
          if (task.depends_on) {
            for (const dep of task.depends_on) {
              if (specIds.has(dep)) {
                taskSpecMap.set(task.id, dep)
                break
              }
            }
          }
        }

        // Second pass: propagate through depends_on chains
        let changed = true
        while (changed) {
          changed = false
          for (const task of tasks) {
            if (taskSpecMap.has(task.id)) continue
            if (task.depends_on) {
              for (const dep of task.depends_on) {
                if (taskSpecMap.has(dep)) {
                  taskSpecMap.set(task.id, taskSpecMap.get(dep)!)
                  changed = true
                  break
                }
              }
            }
          }
        }

        // Group
        const tasksBySpec = new Map<string, Shard[]>()
        const orphans: Shard[] = []
        for (const task of tasks) {
          const specId = taskSpecMap.get(task.id)
          if (specId) {
            const existing = tasksBySpec.get(specId) || []
            existing.push(task)
            tasksBySpec.set(specId, existing)
          } else {
            orphans.push(task)
          }
        }

        for (const spec of specs) {
          groups.push({ spec, tasks: tasksBySpec.get(spec.id) || [] })
        }

        // Remaining orphans: assign to first spec if they have no other home
        if (orphans.length > 0 && specs.length > 0) {
          groups[0]!.tasks.push(...orphans)
        }
      } else {
        // No specs at all — just show tasks flat
        if (tasks.length > 0) {
          groups.push({
            spec: { id: 'ALL', title: 'All Tasks', status: 'TODO' as ShardStatus } as Shard,
            tasks,
          })
        }
      }
    }

    return groups
  }, [shards])

  // Build dependency map for visualization
  const depEdges = useMemo(() => {
    const edges: { from: string; to: string; color: string }[] = []
    let colorIdx = 0
    for (const shard of shards) {
      if (shard.depends_on && shard.depends_on.length > 0) {
        for (const dep of shard.depends_on) {
          // Only show edges where dep is a TASK (not a SPEC)
          if (!dep.startsWith('SPEC')) {
            edges.push({ from: dep, to: shard.id, color: DEP_COLORS[colorIdx % DEP_COLORS.length]! })
            colorIdx++
          }
        }
      }
    }
    return edges
  }, [shards])

  // Recalculate dependency line positions after render
  const updateDepLines = useCallback(() => {
    if (!treeRef.current) return
    const treeRect = treeRef.current.getBoundingClientRect()
    const scrollTop = treeRef.current.scrollTop
    setTreeScrollHeight(treeRef.current.scrollHeight)
    const lines: typeof depLines = []

    for (const edge of depEdges) {
      const fromEl = rowRefs.current.get(edge.from)
      const toEl = rowRefs.current.get(edge.to)
      if (fromEl && toEl) {
        const fromRect = fromEl.getBoundingClientRect()
        const toRect = toEl.getBoundingClientRect()
        lines.push({
          from: edge.from,
          to: edge.to,
          color: edge.color,
          y1: fromRect.top - treeRect.top + scrollTop + fromRect.height / 2,
          y2: toRect.top - treeRect.top + scrollTop + toRect.height / 2,
        })
      }
    }

    setDepLines(lines)
  }, [depEdges])

  useEffect(() => {
    updateDepLines()
    const timer = setInterval(updateDepLines, 500) // refresh positions periodically
    return () => clearInterval(timer)
  }, [updateDepLines, collapsed, filter, shards])

  // Filter
  const filteredGroups = useMemo(() => {
    if (!filter) return specGroups
    const lower = filter.toLowerCase()
    return specGroups
      .map(g => ({
        ...g,
        tasks: g.tasks.filter(t =>
          t.id.toLowerCase().includes(lower) ||
          t.title.toLowerCase().includes(lower) ||
          (t.assigned_worker || '').toLowerCase().includes(lower)
        ),
      }))
      .filter(g =>
        g.spec.id.toLowerCase().includes(lower) ||
        g.spec.title.toLowerCase().includes(lower) ||
        g.tasks.length > 0
      )
  }, [specGroups, filter])

  const selectedShard = shards.find(s => s.id === selectedShardId) || null

  const toggleCollapse = (specId: string) => {
    setCollapsed(prev => {
      const next = new Set(prev)
      if (next.has(specId)) next.delete(specId)
      else next.add(specId)
      return next
    })
  }

  const scrollToTask = (taskId: string) => {
    // Expand any collapsed group containing this task
    for (const group of specGroups) {
      if (group.tasks.some(t => t.id === taskId) && collapsed.has(group.spec.id)) {
        setCollapsed(prev => {
          const next = new Set(prev)
          next.delete(group.spec.id)
          return next
        })
      }
    }

    // Highlight and scroll
    setHighlightedShardId(taskId)
    setTimeout(() => {
      const el = rowRefs.current.get(taskId)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 100)
    setTimeout(() => setHighlightedShardId(null), 2000)
  }

  // Progress calculation (#343: DECOMPOSED tasks are not counted either way)
  const calcProgress = specProgress

  const resolveWorker = (shard: Shard) => {
    const workerName = shard.assigned_worker || shard.assignee
    return workerName ? workers.find(w => w.name === workerName) : undefined
  }

  // Get dependencies for the detail pane
  const getDepInfo = (shard: Shard) => {
    const blockedBy = shard.depends_on || []
    const blocking = shards.filter(s => s.depends_on?.includes(shard.id)).map(s => s.id)
    return { blockedBy, blocking }
  }

  return (
    <div className="flex max-lg:flex-col h-full min-h-0">
      {/* Left: Tree with dependency lines */}
      <div className="flex-[3] min-w-0 flex flex-col min-h-0 border-r max-lg:border-r-0 max-lg:border-b border-cosmic-border">
        {/* Filter */}
        <div className="px-4 py-2 border-b border-cosmic-border/50">
          <input
            type="text"
            placeholder="Filter by ID, title, or worker..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full bg-cosmic-surface border border-cosmic-border rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-cosmic-nebula"
          />
        </div>

        {/* Tree content with SVG overlay */}
        <div ref={treeRef} className="flex-1 overflow-y-auto relative" onScroll={updateDepLines}>
          {/* SVG dependency lines overlay */}
          {depLines.length > 0 && (
            <svg className="absolute top-0 left-0 w-6 pointer-events-none" style={{ zIndex: 1, height: treeScrollHeight || '100%' }}>
              {depLines.map((line, i) => {
                const midY = (line.y1 + line.y2) / 2
                return (
                  <g key={`${line.from}-${line.to}-${i}`}>
                    {/* Curved connector */}
                    <path
                      d={`M 16 ${line.y1} C 6 ${line.y1}, 6 ${midY}, 6 ${midY} C 6 ${midY}, 6 ${line.y2}, 16 ${line.y2}`}
                      fill="none"
                      stroke={line.color}
                      strokeWidth="1.5"
                      opacity="0.5"
                    />
                    {/* Arrow at target */}
                    <polygon
                      points={`16,${line.y2} 12,${line.y2 - 3} 12,${line.y2 + 3}`}
                      fill={line.color}
                      opacity="0.6"
                    />
                    {/* Dot at source */}
                    <circle cx="16" cy={line.y1} r="2" fill={line.color} opacity="0.6" />
                  </g>
                )
              })}
            </svg>
          )}

          <div className="p-4 space-y-1" style={{ paddingLeft: depLines.length > 0 ? '1.75rem' : '1rem' }}>
            {filteredGroups.map(({ spec, tasks }) => {
              const isCollapsed = collapsed.has(spec.id)
              const progress = calcProgress(tasks)

              return (
                <div key={spec.id}>
                  {/* SPEC row */}
                  <button
                    onClick={() => toggleCollapse(spec.id)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-cosmic-surface/50 text-left group"
                  >
                    <span className="text-gray-500 text-xs w-4">{isCollapsed ? '\u25B6' : '\u25BC'}</span>
                    <span className="text-sm font-semibold text-white truncate flex-1">
                      {spec.id}: {spec.title}
                    </span>
                    {/* Progress bar */}
                    {tasks.length > 0 && (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="w-20 h-1.5 bg-cosmic-surface rounded-full overflow-hidden">
                          <div
                            className="h-full bg-teal-500 rounded-full transition-all"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 w-8 text-right">{progress}%</span>
                      </div>
                    )}
                    <span className={`text-xs px-1.5 py-0.5 rounded ${statusIconColor(spec.status)}`}>
                      {spec.status}
                    </span>
                  </button>

                  {/* TASK rows */}
                  <AnimatePresence>
                    {!isCollapsed && tasks.map(task => {
                      const worker = resolveWorker(task)
                      const isSelected = selectedShardId === task.id
                      const isHighlighted = highlightedShardId === task.id

                      return (
                        <motion.div
                          key={task.id}
                          ref={(el) => { if (el) rowRefs.current.set(task.id, el) }}
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          onClick={() => setSelectedShardId(task.id)}
                          className={`w-full flex items-center gap-2 pl-6 pr-2 py-1.5 rounded text-left text-sm cursor-pointer transition-colors ${
                            isHighlighted
                              ? 'bg-cosmic-nebula/20 border border-cosmic-nebula ring-1 ring-cosmic-nebula/50'
                              : isSelected
                                ? 'bg-cosmic-surface border border-cosmic-border'
                                : 'hover:bg-cosmic-surface/30'
                          }`}
                        >
                          <span className={`w-4 text-center flex-shrink-0 ${statusIconColor(task.status)}`}>
                            {statusIcon(task.status)}
                          </span>
                          <span className="text-gray-400 text-xs font-mono w-16 flex-shrink-0">{task.id}</span>
                          <span className="text-gray-200 truncate flex-1">{task.title}</span>
                          {task.depends_on && task.depends_on.length > 0 && (
                            <span className="flex items-center gap-1 flex-shrink-0 max-lg:flex-shrink max-lg:flex-wrap">
                              {task.depends_on.map(dep => (
                                <button
                                  key={dep}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    scrollToTask(dep)
                                  }}
                                  className="text-xs text-cosmic-plasma/70 hover:text-cosmic-plasma hover:underline font-mono"
                                  title={`Depends on ${dep}`}
                                >
                                  &larr;{dep}
                                </button>
                              ))}
                            </span>
                          )}
                          {worker && (
                            <span className="flex items-center gap-1 flex-shrink-0">
                              <PersonaIcon persona={worker.persona} size="sm" />
                              <span className="text-xs text-gray-500">{worker.name}</span>
                            </span>
                          )}
                        </motion.div>
                      )
                    })}
                  </AnimatePresence>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Right: Detail pane */}
      <div className="flex-[2] min-w-0 overflow-y-auto p-4">
        {selectedShard ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">{selectedShard.id}</h3>
              <button
                onClick={() => onShardClick(selectedShard)}
                className="text-xs text-cosmic-nebula hover:underline"
              >
                Open Inspector
              </button>
            </div>
            <h4 className="text-gray-300">{selectedShard.title}</h4>
            <div className="flex items-center gap-2 text-sm">
              <span className={statusIconColor(selectedShard.status)}>{selectedShard.status}</span>
              {resolveWorker(selectedShard) && (
                <button
                  onClick={() => {
                    const w = resolveWorker(selectedShard)
                    if (w) onWorkerClick(w)
                  }}
                  className="flex items-center gap-1 text-gray-400 hover:text-white"
                >
                  <PersonaIcon persona={resolveWorker(selectedShard)!.persona} size="sm" />
                  {selectedShard.assigned_worker || selectedShard.assignee}
                </button>
              )}
            </div>

            {/* Dependency graph section */}
            {(() => {
              const { blockedBy, blocking } = getDepInfo(selectedShard)
              if (blockedBy.length === 0 && blocking.length === 0) return null
              return (
                <div className="themed-panel border border-cosmic-border rounded-lg p-3 text-sm space-y-2">
                  <h5 className="text-xs text-gray-500 uppercase">Dependencies</h5>
                  {blockedBy.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      <span className="text-xs text-gray-500">Blocked by:</span>
                      {blockedBy.map(dep => {
                        const depShard = shards.find(s => s.id === dep)
                        return (
                          <button
                            key={dep}
                            onClick={() => scrollToTask(dep)}
                            className="text-xs font-mono px-1.5 py-0.5 rounded bg-cosmic-surface border border-cosmic-border text-cosmic-plasma hover:border-cosmic-plasma transition-colors"
                          >
                            {dep}{depShard ? `: ${depShard.title.slice(0, 30)}` : ''}
                          </button>
                        )
                      })}
                    </div>
                  )}
                  {blocking.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      <span className="text-xs text-gray-500">Blocks:</span>
                      {blocking.map(dep => {
                        const depShard = shards.find(s => s.id === dep)
                        return (
                          <button
                            key={dep}
                            onClick={() => scrollToTask(dep)}
                            className="text-xs font-mono px-1.5 py-0.5 rounded bg-cosmic-surface border border-cosmic-border text-teal-400 hover:border-teal-400 transition-colors"
                          >
                            {dep}{depShard ? `: ${depShard.title.slice(0, 30)}` : ''}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })()}

            {selectedShard.spec && (
              <div className="themed-panel border border-cosmic-border rounded-lg p-4 text-sm">
                <div className="prose prose-invert prose-sm max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {selectedShard.spec}
                  </ReactMarkdown>
                </div>
              </div>
            )}
            {selectedShard.scratchpad && (
              <div className="themed-panel border border-cosmic-border rounded-lg p-4 text-sm">
                <h5 className="text-xs text-gray-500 uppercase mb-2">Scratchpad</h5>
                <div className="prose prose-invert prose-sm max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {selectedShard.scratchpad}
                  </ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-600 text-sm">
            Select a task to view details
          </div>
        )}
      </div>
    </div>
  )
}
